import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { View } from 'foundations-components/components/View';
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import CodeSplitEnvWrapper from 'pricing-pages-ui/components/common/env/CodeSplitEnvWrapper';
import { ENTERPRISE as ENTERPRISE_TAB } from 'pricing-pages-ui/constants/routing/Tabs';
import { useCallback, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import { CORE_SEATS_ENTERPRISE, CORE_SEATS_PROFESSIONAL, CRM_SUITE_STARTER_BUNDLE, HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { CRM } from 'self-service-api/constants/UpgradeProducts';
import LoadingScreen from '../components/LoadingScreen';
import BannerSection from '../components/banners/BannerSection';
import IntegrationsSection from '../components/common/IntegrationsSection';
import PhoneSupport from '../components/common/PhoneSupport';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import FooterMicrocopy from '../components/faqs/FooterMicrocopy';
import Question from '../components/faqs/Question';
import SmartCrmFeatureTable from '../components/feature-table/smart-crm-feature-table/SmartCrmFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import SmartCrmTiles from '../components/smart-crm/SmartCrmTiles';
import { SMART_CRM_PAGE } from '../constants/ErrorBoundaryNames';
import { STANDALONE_CORE_SEATS } from '../constants/ReleaseInsights';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { TRACKING_SOURCES } from '../constants/tracking/TrackingSources';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useHasReleaseInsight } from '../hooks/useBulkPurchaseOptions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageParams } from '../hooks/usePageParams';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { useSSM0009ExperimentEffect } from '../utils/experiments/ssm0009';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PRODUCT_LEVELS_MAP = {
  [HUBSPOT_FREE]: FREE,
  [CRM_SUITE_STARTER_BUNDLE]: STARTER,
  [CORE_SEATS_PROFESSIONAL]: PROFESSIONAL,
  [CORE_SEATS_ENTERPRISE]: ENTERPRISE
};
const inAppProductsToShow = [CRM_SUITE_STARTER_BUNDLE, CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE];
const publicStarterProductsToShow = [HUBSPOT_FREE, CRM_SUITE_STARTER_BUNDLE, CORE_SEATS_PROFESSIONAL];
const publicEnterpriseProductsToShow = [CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE];
const ssm0009ProductsToShow = [HUBSPOT_FREE, CRM_SUITE_STARTER_BUNDLE, CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE];
const SmartCrmPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(NavItemEnum.SMART_CRM);
  const isAppReady = useGlobalLoading();
  const isInApp = useAppSelector(isAppAuthSelector);
  const {
    hasReleaseInsight: isUngatedForSmartCrm,
    loading: isLoadingReleaseInsight,
    error: isErrorLoadingReleaseInsight
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const navItem = NavItemEnum.SMART_CRM;
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const faqsConfig = FaqsConfig[navItem];
  const {
    isSSM0009Variant
  } = useSSM0009ExperimentEffect();
  const {
    tab
  } = usePageParams();
  const productsToShow = isInApp ? inAppProductsToShow : isSSM0009Variant ? ssm0009ProductsToShow : tab === ENTERPRISE_TAB ? publicEnterpriseProductsToShow : publicStarterProductsToShow;
  const featureTableProductLevels = productsToShow.map(apiName => {
    return PRODUCT_LEVELS_MAP[apiName];
  });
  const redirectCondition = useCallback(() => {
    if (isLoadingReleaseInsight) return;
    return !isUngatedForSmartCrm || !!isErrorLoadingReleaseInsight;
  }, [isLoadingReleaseInsight, isUngatedForSmartCrm, isErrorLoadingReleaseInsight]);
  useRedirectIfIneligible({
    condition: redirectCondition
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen: navItem
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  if (!isAppReady) return /*#__PURE__*/_jsx(LoadingScreen, {});
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    "data-selenium-id": "page-success",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: 'SMART_CRM_PAGE'
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: navItem,
      children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
        children: [/*#__PURE__*/_jsx(BannerSection, {
          screen: SCREENS.SMART_CRM
        }), /*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: navItem
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: navItem
          })]
        }), /*#__PURE__*/_jsx(SmartCrmTiles, {
          screen: SCREENS.SMART_CRM,
          productsToShow: productsToShow
        }), /*#__PURE__*/_jsx(PageSection, {
          children: /*#__PURE__*/_jsx(SmartCrmFeatureTable, {
            productLevels: featureTableProductLevels,
            screen: SCREENS.SMART_CRM
          })
        }), /*#__PURE__*/_jsx(PageSection, {
          children: /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: TRACKING_SOURCES.SMART_CRM_MIDDLE,
            screen: SCREENS.SMART_CRM,
            upgradeProduct: CRM
          })
        }), /*#__PURE__*/_jsx(IntegrationsSection, {
          nav: navItem
        }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
          publicComponentProps: {
            tab: isSSM0009Variant ? 'starter' : tab,
            screen: SCREENS.SMART_CRM
          }
        }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/common/ValuePropsSection`)
        }), /*#__PURE__*/_jsxs(Faq, {
          children: [faqsConfig.map(_ref => {
            let {
                questionKey
              } = _ref,
              passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
            return /*#__PURE__*/_jsx(Question, Object.assign({
              questionKey: questionKey
            }, passthroughOptions), questionKey);
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: TRACKING_SOURCES.SMART_CRM_FOOTER,
          screen: SCREENS.SMART_CRM,
          upgradeProduct: CRM
        })]
      })
    })]
  });
};
export default withErrorBoundary(SmartCrmPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: SMART_CRM_PAGE
});